import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';


export function useIntegrations() {
    const [integrations, setIntegrations] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [providersLoading, setProvidersLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchIntegrations = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/api/integrations');
            setIntegrations(response.data || []);
        } catch (err) {
            console.error('Error fetching integrations:', err);
            setError('Failed to load integrations');
        } finally {
            setLoading(false);
        }
    }, []);


    const fetchProviders = useCallback(async () => {
        try {
            setProvidersLoading(true);
            const response = await api.get('/api/integrations/providers');
            setProviders(response.data || []);
        } catch (err) {
            console.error('Error fetching providers:', err);
        } finally {
            setProvidersLoading(false);
        }
    }, []);

  
    const createIntegration = async (providerKey, displayLabel) => {
        try {
            const response = await api.post(`/api/integrations/${providerKey}/connect`, {
                display_label: displayLabel
            });
            await fetchIntegrations();
            return response.data;
        } catch (err) {
            console.error('Error creating integration:', err);
            throw err;
        }
    };

 
    const updateLabel = async (integrationId, newLabel) => {
        try {
            await api.put(`/api/integrations/${integrationId}`, {
                display_label: newLabel
            });
            await fetchIntegrations();
        } catch (err) {
            console.error('Error updating integration label:', err);
            throw err;
        }
    };

   
    const deleteIntegration = async (integrationId) => {
        try {
            await api.delete(`/api/integrations/${integrationId}`);
            await fetchIntegrations(); // Refresh the list
        } catch (err) {
            console.error('Error deleting integration:', err);
            throw err;
        }
    };

  
    const getIntegrationsByType = useCallback((type, status = null) => {
        let filtered = integrations.filter(i => i.type === type);
        if (status) {
            filtered = filtered.filter(i => i.status === status);
        }
        return filtered;
    }, [integrations]);

   
    const getGroupedIntegrations = useCallback(() => {
        const grouped = {};
        integrations.forEach(integration => {
            const type = integration.type || 'other';
            if (!grouped[type]) {
                grouped[type] = [];
            }
            grouped[type].push(integration);
        });
        return grouped;
    }, [integrations]);

    useEffect(() => {
        fetchIntegrations();
        fetchProviders();
    }, [fetchIntegrations, fetchProviders]);

    return {
        integrations,
        providers,
        loading,
        providersLoading,
        error,
        fetchIntegrations,
        fetchProviders,
        createIntegration,
        updateLabel,
        deleteIntegration,
        getIntegrationsByType,
        getGroupedIntegrations
    };
}
